import React, { useEffect, useState } from 'react';
import { useRestClient } from '../../api/RestClientContext';
import { ShareReferralButton } from '../../shared/components/buttons/ShareReferralButton';
import FullScreenLoader from '../../shared/components/FullScreenLoader';
import LogoBar from '../../shared/components/LogoBar';
import './LoyaltyScreen.css';

interface LoyaltyStep {
    number: number;
    text: React.ReactNode;
    delay: string;
}

const maxBonusUsers = 6;

const LOYALTY_STEPS: LoyaltyStep[] = [
    {
        number: 1,
        text: '👨‍💻 Поделитесь ссылкой с друзьями.',
        delay: '150ms'
    },
    {
        number: 2,
        text: '✍️ Они регистрируются и оформляют подписку.',
        delay: '200ms'
    },
    {
        number: 3,
        text: <>⭐ Вы получаете продление подписки на <span style={{ color: 'var(--color-accent)' }}>15 дней</span>.</>,
        delay: '250ms'
    },
    {
        number: 4,
        text: <>💳 Если друг <span style={{ color: 'var(--color-accent)' }}>оплатит</span> подписку то вы <span style={{ color: 'var(--color-accent)' }}>дополнительно</span> получите <span style={{ color: 'var(--color-accent)' }}>еще 30 дней</span> бесплатно.</>,
        delay: '300ms'
    },
    {
        number: 5,
        text: <>🏆 Всего по реферальной ссылке можно пригласить до <strong>{maxBonusUsers} друзей</strong>, за которых вы получите бонус (в том числе пункт 3 и 4).</>,
        delay: '350ms'
    }
];

interface LoyaltyStepsProps {
    steps?: LoyaltyStep[];
}

const LoyaltySteps: React.FC<LoyaltyStepsProps> = ({ steps = LOYALTY_STEPS }) => {
    return (
        <div className="loyalty-steps">
            {steps.map((step) => (
                <div
                    key={step.number}
                    className="loyalty-step glass-ui stagger-item"
                    style={{ '--animation-delay': step.delay } as React.CSSProperties}
                >
                    <div className="loyalty-step-number">{step.number}</div>
                    <div className="loyalty-step-content">
                        <p className="loyalty-step-text">{step.text}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

interface ReferralProgressProps {
    count: number;
    max: number;
}

const ReferralProgress: React.FC<ReferralProgressProps> = ({ count, max }) => {
    return (
        <div className="referral-progress stagger-item" style={{ '--animation-delay': '50ms' } as React.CSSProperties}>
            <div className="referral-count-display">
                <span className="referral-count-number">{count}</span>
                <span className="referral-count-divider">/{max}</span>
                <span className="referral-count-label">друзей приглашено</span>
            </div>
            <div className="referral-dots">
                {Array.from({ length: max }).map((_, i) => (
                    <div
                        key={`dot-${i}`}
                        className={`referral-dot${i < count ? ' referral-dot--filled' : ''}`}
                    />
                ))}
            </div>
            {count > 0 && (
                <p className="referral-earned">
                    Получено дней подписки: <span className="referral-earned-value">+{count * 30}</span>
                </p>
            )}
        </div>
    );
};

const LoyaltyScreen: React.FC = () => {
    const restClient = useRestClient();
    const [referralsCount, setReferralsCount] = useState<number>(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadReferralData = async () => {
            try {
                setLoading(true);
                const countResponse = await restClient.getReferralsCount();

                if (countResponse.result !== undefined) {
                    setReferralsCount(countResponse.result);
                }
            } catch (error) {
                console.error('Ошибка при загрузке реферальных данных:', error);
            } finally {
                setLoading(false);
            }
        };

        loadReferralData();
    }, [restClient]);

    if (loading)
        return <FullScreenLoader />

    return (
        <div className="loyalty-screen">
            <LogoBar />
            <h2 className="stagger-item" style={{ '--animation-delay': '0ms' } as React.CSSProperties}>Подарки</h2>

            <ReferralProgress count={referralsCount} max={maxBonusUsers} />

            <div className="stagger-item" style={{ '--animation-delay': '80ms' } as React.CSSProperties}>
                <h3>Ваша реферальная ссылка:</h3>
                <div className="dashboard-action-buttons">
                    <ShareReferralButton />
                </div>
            </div>

            <div style={{ marginTop: 32, '--animation-delay': '100ms' } as React.CSSProperties} className="stagger-item">
                <h3 style={{ marginBottom: 20 }}>Как это работает</h3>
                <LoyaltySteps />
            </div>
        </div>
    )
}

export default LoyaltyScreen;
